/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React from "react";
import { useState, useContext } from "react";
import { PatientsContext } from ".";
import { useEffect } from "react";
import fetchHelper from "../../services/fetchHelper";
import { useParams } from "react-router-dom";
import spinner from "../../assets/esign-ui-icons/saving-ico-white.svg";
import { LayoutContext } from "../../components/layout";
import "./Notes.css";
import TasksTable from "./TasksTable";
import { featureCheck } from "../../utils/feature";

const NoteRow = ({ data }) => {
  const [item, setItem] = useState(data);
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isChecked, setIsChecked] = useState(data.sticky);
  const [message, setMessage] = useState(data.note);
  const { fetchPatientNotes } = useContext(PatientsContext);
  const [noteValidationMsg, setNoteValidatinMsg] = useState(false);
  const { i } = useParams();

  const onCancel = () => {
    setNoteValidatinMsg(false);
    setIsEdit(false);
    setIsChecked(data.sticky);
  };

  useEffect(() => {
    if (isEdit) {
      setMessage(data.note);
    }
  }, [isEdit]);

  const submit = async (e) => {
    if (message.length < 1) {
      setNoteValidatinMsg(true);
      return false;
    } else {
      setNoteValidatinMsg(false);
    }
    e.preventDefault();
    if (saving) return false;
    setSaving(true);
    try {
      const body = { ...item, patient: { id: i }, note: message, sticky: isChecked, id: item.id };
      const response = await fetchHelper.put("/patientmanagement/rest/patientnote/" + item.id, body);
      setItem(response);
      setIsEdit(false);
      fetchPatientNotes();
    } catch (err) {
      console.log(err.message);
    }
    setSaving(false);
  };
  if (isEdit) {
    return (
      <tr>
        <td colSpan="100%">
          <div style={{ display: "flex", alignItems: "center" }}>
            <label className="form-li" style={{ width: "auto" }}>
              <input
                type="checkbox"
                className="chk"
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
              />
              <span className="chkmrk" style={{ width: 10, marginBottom: 0 }}></span>
            </label>
            <span style={{ marginBottom: 5 }}>High-Priority? Priority notes will always show at top.</span>
          </div>
          <div className="row rowStretch location-row">
            <div className="col col-s-100 col-m-100 col50">
              <label>
                <span>Lead Type</span>
                <textarea
                  placeholder="Enter note..."
                  className="fullWidth fixed-size"
                  onChange={(e) => setMessage(e.target.value)}
                  value={message}
                />
              </label>
            </div>
            {noteValidationMsg ? (
              <span className="error">Required fields missing data. Please review and re-submit.</span>
            ) : null}
            <div className="col col-s-100 col-m-100 col50 showBs" />
          </div>
          <div style={{ display: "inline-flex" }}>
            <img id="loading-spinner-1" src={spinner} style={{ width: 1, opacity: 0 }} />
            <button className="btn btnTeal btn-sm" onClick={submit}>
              {saving ? <img id="loading-spinner-2" src={spinner} style={{ width: 15 }} /> : "Save"}
            </button>
            <button className="btn btn-sm" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td>
        <label className="form-li">
          <input type="checkbox" className="chk" checked={item.sticky} />
          <span className="chkmrk" style={{ width: 10 }}></span>
        </label>
      </td>
      <td>{new Date(item.noteDate || new Date()).toStandardDate()}</td>
      <td>
        {item?.user?.name} {item?.user?.family}
      </td>
      <td>{item.cat}</td>
      <td style={{ padding: 10 }}>{item.note}</td>
      <td className="alignCenter showBs">
        <button
          className="btn btnSm btnTealL fullWidth"
          onClick={(e) => {
            e.preventDefault();
            setIsEdit(true);
          }}
        >
          Edit
        </button>
      </td>
    </tr>
  );
};

const Notes = () => {
  const {
    fetchPatientNotes,
    fetchDeviceInfo,
    fetchReasons,
    patientNotes,
    addPatientNotes,
    savePatientNote,
    setSavePatientNote,
    setPatientNotes,
  } = useContext(PatientsContext);
  const [saving, setSaving] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [cat, setCat] = useState("clinical");
  const [message, setMessage] = useState("");
  const { user } = useContext(LayoutContext);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [noteValidationMsg, setNoteValidatinMsg] = useState(false);
  const { i } = useParams();
  const [patientEngagementTask, setPatientEngagementTask] = useState(false);

  useEffect(() => {
    fetchPatientNotes();
    fetchDeviceInfo();
    fetchReasons();
    enablePatientEngagementTask();
  }, []);

  useEffect(() => {
    setMessage("");
    setNoteValidatinMsg(false);
    setIsChecked(false);
    setCat("clinical");
  }, [patientNotes.openForm]);

  useEffect(() => {
    if (savePatientNote) {
      submit();
      setSavePatientNote(false);
    }
  }, [savePatientNote]);

  useEffect(() => {
    setPatientNotes({ ...patientNotes, saving: saving });
  }, [saving]);

  /** To Enable or Disable Task Queues Tab */
  const enablePatientEngagementTask = () => {
    setPatientEngagementTask(featureCheck("patientEngagementFeature.enable"));
  };

  const submit = async () => {
    if (message.length < 1) {
      setNoteValidatinMsg(true);
      return false;
    } else {
      setNoteValidatinMsg(false);
    }
    if (saving) return false;
    setSaving(true);
    try {
      const body = { patient: { id: i }, note: message, sticky: isChecked, user, cat };
      const response = await fetchHelper.post("/patientmanagement/rest/patientnote/", body);
      addPatientNotes(response);
    } catch (err) {
      console.log(err.message);
    }
    setSaving(false);
    setMessage("");
  };

  return (
    <>
      <div id="notes-container" className="tabContent location">
        <div className="content">
          <div className="grpHdr">
            <h3>Patient Notes</h3>
          </div>
          {patientNotes.openForm && (
            <div className="row rowStretch location-row">
              <div className="col col-s-100 col-m-100 col100">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <label className="form-li" style={{ width: "auto" }}>
                    <input
                      type="checkbox"
                      className="chk"
                      checked={isChecked}
                      onChange={() => setIsChecked(!isChecked)}
                    />
                    <span className="chkmrk" style={{ width: 10, marginBottom: 0 }}></span>
                  </label>
                  <span style={{ marginBottom: 5 }}>
                    High-Priority? Priority notes will always show at top.
                  </span>
                </div>
              </div>
              <div className="col col-s-100 col-m-50 col50" style={{ paddingLeft: 0 }}>
                <label className="inCal">
                  <select name="category" value={cat} onChange={(e) => setCat(e.target.value)}>
                    <option value="clinical">Clinical</option>
                    <option value="pe">PE</option>
                    <option value="admin">Admin</option>
                  </select>
                </label>
              </div>
              <div className="col col-s-100 col-m-50 col50" />
              <div className="col col-s-100 col-m-100 col100">
                <label>
                  <textarea
                    placeholder="Enter note..."
                    className="fullWidth fixed-size"
                    defaultValue={""}
                    onChange={(e) => {
                      e.preventDefault();
                      setMessage(e.target.value);
                    }}
                  />
                </label>
              </div>
              {noteValidationMsg && (
                <span className="error">Required fields missing data. Please review and re-submit.</span>
              )}
              <div className="col col-s-100 col-m-100 col50 showBs" />
            </div>
          )}
          <table className="overview" id="data-table">
            <thead>
              <tr style={{ textAlign: "left", background: "#f7f7f7" }}>
                <th>High-Priority</th>
                <th style={{ minWidth: 100 }}>Date</th>
                <th style={{ minWidth: 130 }}>User</th>
                <th>Category</th>
                <th>Note</th>
                <th className="showBs" />
              </tr>
            </thead>
            <tbody>
              {patientNotes.list
                .sort((a, b) => (a.sticky ? -1 : 1))
                .sort((a, b) => {
                  if (a.sticky === b.sticky) {
                    return b.noteDate - a.noteDate;
                  }
                })
                .map((item) => (
                  <NoteRow data={item} key={item.id} />
                ))}
            </tbody>
          </table>
        </div>
        {patientEngagementTask ? (
          <TasksTable shouldRefresh={shouldRefresh} setShouldRefresh={setShouldRefresh} />
        ) : null}
      </div>
    </>
  );
};

export default Notes;
